import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { apiGet, apiPost } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtTime, fmtBytes } from '@/lib/format';
import { order } from '@/lib/utils';
import { EVENT_PAGE_SIZE, eventDateBounds, eventSearchQuery } from '@/lib/eventSearch';
import { evName, mediaName, mediaVariant, reviewName, reviewVariant, REVIEW_OPTIONS } from '@/lib/labels';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/DataTable';
import { ConfidenceBar } from '@/components/Stats';
import { Drawer } from '@/components/Drawer';
import { BulkBar, RowCheckbox } from '@/components/BulkBar';
import { MediaInsights } from '@/components/MediaInsights';
import type { EventListItem, EventListResponse, EventDetail, EventMediaFile, Device, Site } from '@/types';

/** 篩選用的狀態選項 */
const STATE_FILTERS = [{ label: '全部', value: '' }, ...REVIEW_OPTIONS.map((r) => ({ label: r.label, value: r.key }))];

/** 事件類型中文名稱 */
/* 事件類型翻譯改用 @/lib/labels 的共用表：
   那份的值來自原生版 app.js，與平台實際送出的類型一致。 */

export default function EventsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const stateFilter = searchParams.get('state') ?? '';
  const deviceFilter = searchParams.get('device') ?? '';
  const siteFilter = searchParams.get('site') ?? '';
  const resolutionFilter = searchParams.get('site_resolution') ?? '';
  const mediaFilter = searchParams.get('media') ?? '';
  const fromDate = searchParams.get('from') ?? '';
  const toDate = searchParams.get('to') ?? '';
  const requestedPage = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const page = Number.isFinite(requestedPage) && requestedPage > 0 ? requestedPage - 1 : 0;
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState('');
  const [reviewNotice, setReviewNotice] = useState('');
  const listHeadingRef = useRef<HTMLDivElement>(null);
  /* ── 批次選取 ──
   * selected 只裝當頁勾的 id；allMatching 代表「全部符合篩選的」，
   * 這時不列舉 id 而是把篩選條件送給後端——2,900 筆的 id 塞不進請求，
   * 而且前端也只看得到當頁。 */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const clearSelection = useCallback(() => { setSelected(new Set()); setAllMatching(false); }, []);

  /* 換頁或換篩選就清空選取：畫面上的資料換了，沿用舊選取會動到看不見的列 */
  const changeFilter = (key: string, value: string) => {
    setSelected(new Set());
    setAllMatching(false);
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setSearchParams(next);
  };
  const clearFilters = () => {
    setSelected(new Set());
    setAllMatching(false);
    setSearchParams(new URLSearchParams());
  };
  const invalidDates = !!(fromDate && toDate && fromDate > toDate);
  const { dateFrom: fromTime, dateTo: toTime } = eventDateBounds(fromDate, toDate);

  const devicesQ = useQuery({ queryKey: ['devices'], queryFn: () => apiGet<Device[]>('/api/v1/devices') });
  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => apiGet<Site[]>('/api/v1/sites') });

  /* ── 事件清單 ── */
  const listQuery = useQuery({
    queryKey: ['events', stateFilter, deviceFilter, siteFilter, resolutionFilter, mediaFilter, fromTime, toTime, page],
    queryFn: () => {
      const q = new URLSearchParams(eventSearchQuery({ page, state: stateFilter, device: deviceFilter, site: siteFilter, siteResolution: resolutionFilter, dateFrom: fromTime, dateTo: toTime }));
      if (mediaFilter) q.set('media', mediaFilter);
      return apiGet<EventListResponse>(`/api/v1/events?${q}`);
    },
    enabled: !invalidDates,
    refetchInterval: 15_000,
  });

  const rows = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const first = total && page * EVENT_PAGE_SIZE < total ? page * EVENT_PAGE_SIZE + 1 : 0;
  const last = Math.min((page + 1) * EVENT_PAGE_SIZE, total);
  const filterCount = [stateFilter, deviceFilter, siteFilter, resolutionFilter, mediaFilter, fromDate, toDate].filter(Boolean).length;

  useEffect(() => {
    if (listQuery.isSuccess && page > 0 && page * EVENT_PAGE_SIZE >= total) {
      const next = new URLSearchParams(searchParams);
      next.set('page', String(Math.max(1, Math.ceil(total / EVENT_PAGE_SIZE))));
      setSearchParams(next, { replace: true });
    }
  }, [listQuery.isSuccess, page, searchParams, setSearchParams, total]);

  const changePage = (nextPage: number) => {
    setSelected(new Set());
    setAllMatching(false);
    const next = new URLSearchParams(searchParams);
    if (nextPage > 0) next.set('page', String(nextPage + 1)); else next.delete('page');
    setSearchParams(next);
    listHeadingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ── 事件詳情 ── */
  const detailQuery = useQuery({
    queryKey: ['event-detail', selectedEventId],
    queryFn: () =>
      apiGet<EventDetail>(
        `/api/v1/events/${encodeURIComponent(selectedEventId!)}`,
      ),
    enabled: selectedEventId !== null,
  });

  /* ── 人工判讀 ── */
  const reviewMutation = useMutation({
    mutationFn: (vars: { eventId: string; review_state: string }) =>
      apiPost(`/api/v1/events/${encodeURIComponent(vars.eventId)}/review`, {
        review_state: vars.review_state,
      }),
    onSuccess: () => {
      setSelectedEventId(null);
      setReviewError('');
      setReviewNotice('判讀狀態已更新。');
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event-detail'] });
    },
    onError: (err: Error) => {
      setReviewError(err.message);
    },
  });

  /* ── 批次標註 ──
   * 一次請求完成，不是迴圈打單筆端點：上千筆逐一發請求會打爆伺服器與瀏覽器連線數。 */
  const bulkMutation = useMutation({
    mutationFn: (review_state: string) =>
      apiPost('/api/v1/events/bulk-review', allMatching
        ? { review_state, filter: { state: stateFilter || undefined, device: deviceFilter || undefined,
                                    site: siteFilter || undefined,
                                    site_resolution: resolutionFilter || undefined, media: mediaFilter || undefined,
                                    date_from: fromTime || undefined, date_to: toTime || undefined } }
        : { review_state, event_ids: [...selected] }),
    onSuccess: (res: unknown) => {
      const n = (res as { updated?: number })?.updated ?? 0;
      setReviewError('');
      setReviewNotice(`已更新 ${n.toLocaleString()} 筆判讀狀態。`);
      clearSelection();
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: (err: Error) => setReviewError(err.message),
  });

  const handleReview = useCallback(
    (eventId: string, review_state: string) => {
      setReviewError('');
      setReviewNotice('');
      reviewMutation.mutate({ eventId, review_state });
    },
    [reviewMutation],
  );

  /* ── 表格欄位 ── */
  const pageAllSelected = rows.length > 0 && rows.every((e) => selected.has(e.event_id));
  const toggleRow = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });

  const columns: Column<EventListItem>[] = [
    {
      key: 'select',
      header: (
        <RowCheckbox
          checked={pageAllSelected}
          onChange={(on) => { setAllMatching(false); setSelected(on ? new Set(rows.map((e) => e.event_id)) : new Set()); }}
          label="全選本頁"
        />
      ),
      className: 'w-9',
      cell: (e) => (
        <RowCheckbox
          checked={allMatching || selected.has(e.event_id)}
          onChange={(on) => { setAllMatching(false); toggleRow(e.event_id, on); }}
          label={`選取 ${e.event_id}`}
        />
      ),
    },
    {
      key: 'occurred_at',
      header: '發生時間',
      cell: (e) => (
        <span className="font-mono text-[11.5px]">
          {fmtTime(e.occurred_at ?? e.received_at)}
          {e.clock_synced === false && (
            <Badge variant="warn" className="ml-1">時鐘未同步</Badge>
          )}
        </span>
      ),
    },
    {
      key: 'device_key',
      header: '裝置',
      cell: (e) => <span className="font-mono text-[11.5px]">{e.device_key}</span>,
    },
    {
      key: 'site_code',
      header: '場域',
      cell: (e) =>
        e.site_resolution === 'pending' ? (
          <Badge variant="warn">待判定</Badge>
        ) : (
          <span>{e.site_code ?? '—'}</span>
        ),
    },
    {
      key: 'event_type',
      header: '類型',
      cell: (e) => evName(e.event_type),
    },
    {
      key: 'confidence',
      header: '信心',
      align: 'right',
      cell: (e) => <ConfidenceBar value={e.confidence != null ? Number(e.confidence) : null} />,
    },
    {
      key: 'media',
      header: '影音',
      cell: (e) =>
        (e.media_total ?? 0) > 0 ? (
          <Badge variant={e.media_stored === e.media_total ? 'success' : 'info'}>
            {e.media_stored ?? 0}/{e.media_total}
          </Badge>
        ) : (
          '—'
        ),
    },
    {
      key: 'review_state',
      header: '狀態',
      cell: (e) => {
        const s = e.review_state ?? 'unreviewed';
        return (
          <Badge variant={reviewVariant(s)}>
            {reviewName(s)}
          </Badge>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      cell: (e) => (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelectedEventId(e.event_id)}
          >
            詳情
          </Button>
        </div>
      ),
    },
  ];

  /* ── 抽屜內容 ── */
  const detail = detailQuery.data;
  const advancedFilters = (
    <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
       <label className="flex min-w-0 flex-col gap-1 text-[12px] text-muted-foreground">平台接收日期・起
        <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => changeFilter('from', e.target.value)} className="h-11 w-full min-w-0 rounded-sm border border-border bg-background px-2 text-base text-foreground lg:h-9 lg:text-sm" />
      </label>
      <label className="flex min-w-0 flex-col gap-1 text-[12px] text-muted-foreground">平台接收日期・迄
        <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => changeFilter('to', e.target.value)} className="h-11 w-full min-w-0 rounded-sm border border-border bg-background px-2 text-base text-foreground lg:h-9 lg:text-sm" />
      </label>
      <label className="flex min-w-0 flex-col gap-1 text-[12px] text-muted-foreground">裝置
        <select value={deviceFilter} onChange={(e) => changeFilter('device', e.target.value)} className="h-11 min-w-0 rounded-sm border border-border bg-background px-2 text-base text-foreground lg:h-9 lg:text-sm">
          <option value="">全部裝置</option>
          {devicesQ.data?.map((d) => <option key={d.device_id} value={d.device_id}>{d.label ? `${d.label} (${d.device_id})` : d.device_id}</option>)}
        </select>
      </label>
      <label className="flex min-w-0 flex-col gap-1 text-[12px] text-muted-foreground">場域
        <select value={siteFilter} onChange={(e) => changeFilter('site', e.target.value)} className="h-11 min-w-0 rounded-sm border border-border bg-background px-2 text-base text-foreground lg:h-9 lg:text-sm">
          <option value="">全部場域</option>
          {sitesQ.data?.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
        </select>
      </label>
      <div className="flex min-h-11 flex-wrap items-end gap-2">
        {resolutionFilter === 'pending' && <Badge variant="warn">場域待判定</Badge>}
        <Button size="sm" variant="ghost" onClick={clearFilters}>清除篩選</Button>
      </div>
    </div>
  );

  return (
    <>
      {/* 篩選列 */}
      <div className="reveal mb-3 flex flex-wrap gap-2" style={order(0)} role="group" aria-label="事件狀態篩選">
        {STATE_FILTERS.map((f) => (
          <Button
            key={f.value}
            size="sm"
            variant={stateFilter === f.value ? 'default' : 'secondary'}
            aria-pressed={stateFilter === f.value}
            onClick={() => changeFilter('state', f.value)}
          >
            <span className="whitespace-nowrap">{f.label}</span>
          </Button>
        ))}
      </div>
      <div className="reveal mb-3 flex flex-wrap gap-2" style={order(1)} role="group" aria-label="影音可判讀狀態篩選">
        {[['', '全部影音'], ['ready', '可判讀'], ['pending', '影音待上傳']].map(([value, label]) => (
          <Button key={value} size="sm" variant={mediaFilter === value ? 'default' : 'secondary'}
            aria-pressed={mediaFilter === value} onClick={() => changeFilter('media', value)}>
            {label}
          </Button>
        ))}
      </div>
      <details className="reveal mb-3 rounded-lg border border-border bg-card lg:hidden" style={order(2)}>
        <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between px-4 text-sm font-medium">
          <span>更多篩選</span>
          <span className="text-[12px] text-muted-foreground">{filterCount ? `已套用 ${filterCount} 項` : '未套用'}</span>
        </summary>
        {advancedFilters}
      </details>
      {filterCount > 0 && (
        <div className="mb-3 flex min-w-0 flex-wrap items-center gap-2 text-xs" aria-label="已套用篩選">
          <span className="font-medium text-foreground">篩選中</span>
          {stateFilter && <Badge variant="muted">{STATE_FILTERS.find((f) => f.value === stateFilter)?.label ?? stateFilter}</Badge>}
          {deviceFilter && <Badge variant="muted" className="max-w-full truncate" title={deviceFilter}>裝置 {deviceFilter}</Badge>}
          {siteFilter && <Badge variant="muted">場域 {siteFilter}</Badge>}
          {mediaFilter && <Badge variant={mediaFilter === 'ready' ? 'success' : 'info'}>{mediaFilter === 'ready' ? '影音可判讀' : '影音待上傳'}</Badge>}
          {(fromDate || toDate) && <Badge variant="muted">日期 {fromDate || '不限'}–{toDate || '不限'}</Badge>}
          <button className="min-h-11 px-2 font-medium text-primary underline-offset-4 hover:underline" onClick={clearFilters}>清除</button>
        </div>
      )}
      <div className="reveal mb-4 hidden rounded-lg border border-border bg-card lg:block" style={order(2)}>{advancedFilters}</div>
      {invalidDates && <p className="mb-4 text-[13px] text-destructive">結束日期不能早於起始日期。</p>}
      {reviewNotice && <p className="callout mb-4 text-[13px] text-success-on" role="status">{reviewNotice}</p>}

      {/* 事件清單 */}
      <Card className="reveal overflow-hidden" style={order(3)}>
        <CardHeader ref={listHeadingRef} className="scroll-mt-4 border-b border-border px-4 py-4 sm:px-6">
          <div>
            <CardTitle>事件（{total}）</CardTitle>
            <CardDescription>依最新平台接收時間排序，顯示 {first}–{last}／{total} 筆</CardDescription>
          </div>
        </CardHeader>
        <div className="hidden lg:block"><DataTable
          rows={rows}
          columns={columns}
          rowKey={(e) => e.event_id}
          loading={listQuery.isLoading}
          error={listQuery.error}
          empty={
            stateFilter
              ? '切換其他狀態查看事件紀錄。'
              : '新的邊緣偵測事件會出現在這裡。'
          }
        /></div>
        <div className="lg:hidden">
          <div className="flex min-h-14 items-center justify-between gap-3 border-b border-border bg-secondary/25 px-3 sm:px-4">
            <div className="flex min-h-11 items-center gap-2 text-sm font-medium">
              <RowCheckbox checked={pageAllSelected}
                onChange={(on) => { setAllMatching(false); setSelected(on ? new Set(rows.map((event) => event.event_id)) : new Set()); }}
                label="全選本頁" />
              <span>全選本頁</span>
            </div>
            <span className="text-xs text-muted-foreground" aria-live="polite">
              {allMatching ? `已選全部 ${total.toLocaleString()} 筆` : selected.size ? `已選 ${selected.size.toLocaleString()} 筆` : `本頁 ${rows.length.toLocaleString()} 筆`}
            </span>
          </div>
          {listQuery.isLoading ? <p className="p-6 text-sm text-muted-foreground">載入事件中…</p> : listQuery.error ? <p className="p-6 text-sm text-destructive">載入失敗：{listQuery.error instanceof Error ? listQuery.error.message : '未知錯誤'}</p> : rows.length === 0 ? <p className="p-6 text-sm text-muted-foreground">沒有符合條件的事件。可清除篩選再查看。</p> : (
            <div className="divide-y divide-border">
              {rows.map((event) => <div key={event.event_id} className="grid grid-cols-[44px_minmax(0,1fr)_44px] items-start gap-1 px-2 py-2.5 sm:gap-2 sm:px-4">
                <div className="pt-1">
                  <RowCheckbox
                    checked={allMatching || selected.has(event.event_id)}
                    onChange={(on) => { setAllMatching(false); toggleRow(event.event_id, on); }}
                    label={`選取 ${event.event_id}`}
                  />
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2"><span className="text-sm font-semibold">{evName(event.event_type)}</span><Badge variant={reviewVariant(event.review_state ?? 'unreviewed')}>{reviewName(event.review_state ?? 'unreviewed')}</Badge></div>
                  <p className="break-words font-mono text-[11px] leading-relaxed text-muted-foreground" title={`${fmtTime(event.received_at)} · ${event.device_key}`}>{fmtTime(event.received_at)} · {event.device_key}</p>
                  <p className="flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground"><span>{event.site_resolution === 'pending' ? '場域待判定' : event.site_code ?? '未指定場域'}</span><Badge variant={(event.media_stored ?? 0) > 0 ? 'success' : 'info'}>{(event.media_stored ?? 0) > 0 ? '可判讀' : '影音待上傳'}</Badge></p>
                </div>
                <Button size="sm" variant="ghost" className="px-0" aria-label={`查看事件 ${event.event_id} 詳情`} onClick={() => setSelectedEventId(event.event_id)}>詳情</Button>
              </div>)}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3 text-[12px] text-muted-foreground">
          <span>{first}–{last}／{total} 筆</span>
          <div className="flex gap-2"><Button size="sm" variant="secondary" disabled={page === 0 || listQuery.isFetching} onClick={() => changePage(page - 1)}>上一頁</Button><Button size="sm" variant="secondary" disabled={last >= total || listQuery.isFetching} onClick={() => changePage(page + 1)}>下一頁</Button></div>
        </div>
      </Card>

      {/* 事件詳情抽屜 */}
      <Drawer
        open={selectedEventId !== null}
        onOpenChange={(v) => {
          if (!v) {
            setSelectedEventId(null);
            setReviewError('');
          }
        }}
        title="事件詳情"
      >
        {detailQuery.isLoading ? (
          <div className="grid place-items-center py-20">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : detailQuery.error ? (
          <p className="text-[13px] text-destructive">
            載入失敗：{detailQuery.error instanceof Error ? detailQuery.error.message : '未知錯誤'}
          </p>
        ) : detail ? (
          <div className="space-y-6">
            {/* 詳情欄位 */}
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-[13px]">
              <dt className="text-muted-foreground">事件編號</dt>
              <dd className="font-mono text-[11.5px]">{detail.event_id}</dd>
              <dt className="text-muted-foreground">裝置</dt>
              <dd className="font-mono text-[11.5px]">{detail.device_key}</dd>
              <dt className="text-muted-foreground">場域</dt>
              <dd>
                {detail.site_code ??
                  (detail.site_resolution === 'pending' ? '待判定' : '—')}
              </dd>
              <dt className="text-muted-foreground">類型</dt>
              <dd>{evName(detail.event_type)}</dd>
              <dt className="text-muted-foreground">信心</dt>
              <dd className="tabular">
                {detail.confidence != null
                  ? Number(detail.confidence).toFixed(3)
                  : '—'}
              </dd>
              <dt className="text-muted-foreground">發生時間</dt>
              <dd className="font-mono text-[11.5px]">{fmtTime(detail.occurred_at)}</dd>
              <dt className="text-muted-foreground">平台接收</dt>
              <dd className="font-mono text-[11.5px]">{fmtTime(detail.received_at)}</dd>
              <dt className="text-muted-foreground">模型版本</dt>
              <dd className="font-mono text-[11.5px]">{detail.model_version ?? '—'}</dd>
            </dl>

            {detail.clock_synced === false && (
              <p className="text-[12px] text-warn">
                此事件的裝置時鐘未同步，發生時間以平台接收時間為準較可靠。
              </p>
            )}

            {/* 影音檔案 */}
            <div>
              <h3 className="mb-2 text-[14px] font-semibold">影音檔案</h3>
              {detail.media.length === 0 ? (
                <p className="text-[13px] text-muted-foreground">此事件沒有影音檔</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table w-full border-collapse text-[13px]">
                    <tbody>
                      {detail.media.map((m: EventMediaFile) => (
                        <tr key={m.id}>
                          {/* 窄螢幕會把表格轉成卡片（index.css 的 720px 規則），
                              這張表沒有表頭，必須靠 data-label 才知道每格是什麼 */}
                          <td data-label="檔案" className="font-mono text-[11.5px]">{m.filename}</td>
                          <td data-label="狀態">
                            <Badge variant={mediaVariant(m.status)}>
                              {mediaName(m.status)}
                            </Badge>
                            {m.backed_up && (
                              <Badge variant="muted" className="ml-1">已備份</Badge>
                            )}
                          </td>
                          <td data-label="大小" className="tabular text-right">
                            {fmtBytes(m.actual_bytes ?? m.expected_bytes)}
                          </td>
                          <td data-label="操作" className="text-right">
                            {m.status === 'stored' ? (
                              <a
                                href={`/api/v1/media/${m.id}/download`}
                                className="text-[12px] text-primary hover:underline"
                              >
                                下載
                              </a>
                            ) : m.fail_reason ? (
                              <span className="text-[11.5px] text-destructive">
                                {m.fail_reason}
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <MediaInsights eventId={detail.event_id} />

            {/* 人工判讀 */}
            {user?.role !== 'viewer' && (() => {
              const hasReviewableMedia = detail.media.some((m) => m.status === 'stored');
              return (
              <div>
                <h3 className="mb-2 text-[14px] font-semibold">人工判讀</h3>
                {!hasReviewableMedia && (
                  <div role="status" className="mb-3 rounded-md border border-info/30 bg-info/10 p-3 text-sm leading-6 text-foreground">
                    <strong className="block">影音尚未保存，現在無法完成證據判讀</strong>
                    <span className="text-muted-foreground">請等待上傳完成後再確認真偽；若必須先分流，可標記為「不確定」。</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => handleReview(detail.event_id, 'confirmed')}
                    disabled={
                      detail.review_state === 'confirmed' ||
                      reviewMutation.isPending || !hasReviewableMedia
                    }
                  >
                    確認為真
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => handleReview(detail.event_id, 'false_positive')}
                    disabled={
                      detail.review_state === 'false_positive' ||
                      reviewMutation.isPending || !hasReviewableMedia
                    }
                  >
                    標記誤報
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handleReview(detail.event_id, 'uncertain')}
                    disabled={detail.review_state === 'uncertain' || reviewMutation.isPending}
                  >
                    標記不確定
                  </Button>
                </div>
                {reviewError && (
                  <p className="mt-2 text-[13px] text-destructive">{reviewError}</p>
                )}
              </div>
              );
            })()}
          </div>
        ) : null}
      </Drawer>

      {/* 批次操作列：有選取才出現，固定在底部方便邊捲邊操作 */}
      <BulkBar
        count={selected.size}
        total={total}
        allMatching={allMatching}
        pageAllSelected={pageAllSelected}
        onSelectAllMatching={() => setAllMatching(true)}
        onClear={clearSelection}
        busy={bulkMutation.isPending}
      >
        {/* 「待確認」也列進來：批次一次動上千筆，誤標時要有從 UI 復原的路。
            排在最後並用 ghost，避免跟真正的判讀動作混淆。 */}
        {user?.role !== 'viewer' && mediaFilter !== 'ready' && (
          <span className="max-w-52 text-xs leading-5 text-muted-foreground">批次真偽判讀前，請先篩選「可判讀」影音。</span>
        )}
        {user?.role !== 'viewer' && [...REVIEW_OPTIONS].sort((a, b) =>
          (a.key === 'unreviewed' ? 1 : 0) - (b.key === 'unreviewed' ? 1 : 0)).map((r) => (
          <Button
            key={r.key}
            size="sm"
            variant={r.key === 'confirmed' ? 'default' : r.key === 'unreviewed' ? 'ghost' : 'secondary'}
            disabled={bulkMutation.isPending || ((r.key === 'confirmed' || r.key === 'false_positive') && mediaFilter !== 'ready')}
            title={(r.key === 'confirmed' || r.key === 'false_positive') && mediaFilter !== 'ready' ? '請先篩選「可判讀」影音，再做批次真偽判讀' : undefined}
            onClick={() => { setReviewError(''); setReviewNotice(''); bulkMutation.mutate(r.key); }}
          >
            {bulkMutation.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            {r.key === 'unreviewed' ? '重設為待確認' : `標記${r.label}`}
          </Button>
        ))}
      </BulkBar>
    </>
  );
}
